import { ReactNode } from 'react';

import cx from 'clsx';

import {
  CheckIcon,
  ScrollArea,
  Combobox,
  ComboboxItem,
  ComboboxParsedItem,
  defaultOptionsFilter,
  OptionsFilter,
  ComboboxParsedItemGroup
} from '@mantine/core';

import { CustomComboboxItem } from '../Select';

import classes from './OptionsDropdown.module.css';

const isEmptyComboboxData = (data: ComboboxParsedItem[]) => {
  if (data.length === 0) {
    return true;
  }

  for (const item of data) {
    if (!('group' in item)) {
      return false;
    }

    if ((item as ComboboxParsedItemGroup).items.length > 0) {
      return false;
    }
  }

  return true;
};
export function isOptionsGroup(
  item: ComboboxParsedItem
): item is ComboboxParsedItemGroup {
  return 'group' in item;
}

export function validateOptions(options: any[], valuesSet = new Set()) {
  if (!Array.isArray(options)) {
    return;
  }

  for (const option of options) {
    if (isOptionsGroup(option)) {
      validateOptions(option.items, valuesSet);
    } else {
      if (typeof option.value === 'undefined') {
        throw new Error('[@mantine/core] Each option must have value property');
      }

      if (typeof option.value !== 'string') {
        throw new Error(
          `[@mantine/core] Option value must be a string, other data formats are not supported, got ${typeof option.value}`
        );
      }

      if (valuesSet.has(option.value)) {
        throw new Error(
          `[@mantine/core] Duplicate options are not supported. Option with value "${option.value}" was provided more than once`
        );
      }

      valuesSet.add(option.value);
    }
  }
}

export interface OptionsGroup {
  group: string;
  items: ComboboxItem[];
}

interface CustomSelectItem {
  label: string;
  value: string;
  color?: string;
  disabled?: boolean;
  name?: string;
}

export type OptionsData = (ComboboxItem | OptionsGroup)[];

interface OptionProps {
  data: CustomSelectItem | OptionsGroup;
  withCheckIcon?: boolean;
  value?: string | number | string[] | null;
  checkIconPosition?: 'left' | 'right';
  unstyled: boolean | undefined;
  itemComponent?: (item: CustomComboboxItem, checked: boolean) => ReactNode;
}

function isValueChecked(
  value: string | number | string[] | undefined | null,
  optionValue: string
) {
  return Array.isArray(value) ? value.includes(optionValue) : value == optionValue;
}

function Option({
  data,
  withCheckIcon,
  value,
  checkIconPosition,
  unstyled,
  itemComponent
}: OptionProps) {
  if (!isOptionsGroup(data)) {
    const check = withCheckIcon && isValueChecked(value, data.value) && (
      <CheckIcon className={classes.optionsDropdownCheckIcon} />
    );

    return (
      <Combobox.Option
        value={data.value}
        disabled={data.disabled}
        className={cx({ [classes.optionsDropdownOption]: !unstyled })}
        data-reverse={checkIconPosition === 'right' || undefined}
        data-checked={isValueChecked(value, data.value) || undefined}
        aria-selected={isValueChecked(value, data.value)}
      >
        {!itemComponent ? (
          <>
            {checkIconPosition === 'left' && check}
            <span>{data.label}</span>
            {checkIconPosition === 'right' && check}
          </>
        ) : (
          itemComponent(
            data as CustomComboboxItem,
            isValueChecked(value, data.value)
          )
        )}
      </Combobox.Option>
    );
  }

  const options = data.items.map((item) => (
    <Option
      data={item}
      value={value}
      key={item.value}
      unstyled={unstyled}
      withCheckIcon={withCheckIcon}
      checkIconPosition={checkIconPosition}
    />
  ));

  return <Combobox.Group label={data.group}>{options}</Combobox.Group>;
}

export interface OptionsDropdownProps {
  data: OptionsData;
  filter: OptionsFilter | undefined;
  search: string | undefined;
  limit: number | undefined;
  withScrollArea: boolean | undefined;
  maxDropdownHeight: number | string | undefined;
  hidden?: boolean;
  hiddenWhenEmpty?: boolean;
  filterOptions?: boolean;
  withCheckIcon?: boolean;
  value?: string | number | string[] | null;
  checkIconPosition?: 'left' | 'right';
  nothingFoundMessage?: React.ReactNode;
  creatable?: boolean;
  getCreateLabel?: (query: string) => string;
  onCreate?: (query: string) => string | number | ComboboxItem;
  setValue: (value: string | number | null) => void;
  unstyled: boolean | undefined;
  labelId: string;
  itemComponent?: (item: CustomComboboxItem, checked: boolean) => ReactNode;
}

export function OptionsDropdown({
  data,
  hidden,
  hiddenWhenEmpty,
  filter,
  search,
  limit,
  maxDropdownHeight,
  withScrollArea = true,
  filterOptions = true,
  withCheckIcon = false,
  value,
  checkIconPosition,
  nothingFoundMessage,
  unstyled,
  labelId,
  creatable,
  getCreateLabel,
  onCreate,
  setValue,
  itemComponent
}: OptionsDropdownProps) {
  validateOptions(data);

  const shouldFilter = typeof search === 'string';
  const filteredData = shouldFilter
    ? (filter || defaultOptionsFilter)({
        options: data,
        search: filterOptions ? search : '',
        limit: limit ?? Infinity
      })
    : data;
  const isEmpty = isEmptyComboboxData(filteredData);

  const options = filteredData.map((item) => (
    <Option
      data={item}
      key={isOptionsGroup(item) ? item.group : item.value}
      withCheckIcon={withCheckIcon}
      value={value}
      checkIconPosition={checkIconPosition}
      unstyled={unstyled}
      itemComponent={itemComponent}
    />
  ));

  return (
    <Combobox.Dropdown hidden={hidden || (hiddenWhenEmpty && isEmpty && !creatable)}>
      <Combobox.Options labelledBy={labelId}>
        {withScrollArea ? (
          <ScrollArea.Autosize
            mah={maxDropdownHeight ?? 220}
            type="scroll"
            scrollbarSize="var(--_combobox-padding)"
            offsetScrollbars="y"
            className={classes.optionsDropdownScrollArea}
          >
            {options}
          </ScrollArea.Autosize>
        ) : (
          options
        )}

        {creatable ? (
          <Combobox.Option
            value="$create"
            mt={'sm'}
            onClick={() => {
              onCreate?.(search || '');
              setValue?.(search || '');
            }}
          >
            {getCreateLabel ? getCreateLabel(search || '') : `Create "${search}"`}
          </Combobox.Option>
        ) : (
          isEmpty &&
          nothingFoundMessage && (
            <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>
          )
        )}
      </Combobox.Options>
    </Combobox.Dropdown>
  );
}
